import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Droplets } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          clearInterval(timer);
        } else if (attempts >= 15) {
          setState("failed");
          clearInterval(timer);
        }
      } catch {
        if (attempts >= 15) {
          setState("failed");
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-obsidian px-6" data-testid="payment-success-page">
      <div className="w-full max-w-md border border-white/10 bg-carbon p-10 text-center">
        <span className="mx-auto flex h-9 w-9 items-center justify-center border border-aqua/50 text-aqua">
          <Droplets size={18} />
        </span>
        {state === "checking" && (
          <>
            <Loader2 size={28} className="mx-auto mt-8 animate-spin text-aqua" />
            <h1 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight text-white">
              Confirming payment
            </h1>
            <p className="mt-3 text-sm text-zinc-400">Hold tight — we're confirming your payment with Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={32} className="mx-auto mt-8 text-aqua" />
            <h1 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight text-white" data-testid="payment-success-heading">
              Booking confirmed
            </h1>
            <p className="mt-3 text-sm text-zinc-400">
              Payment received. Your booking is in and a confirmation is on its way to your inbox.
            </p>
            <Link
              to="/dashboard"
              data-testid="payment-success-dashboard-link"
              className="mt-8 inline-block border border-aqua bg-aqua px-8 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-transparent hover:text-aqua"
            >
              Track my booking
            </Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={32} className="mx-auto mt-8 text-amber-400" />
            <h1 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight text-white" data-testid="payment-pending-heading">
              Still confirming
            </h1>
            <p className="mt-3 text-sm text-zinc-400">
              Your payment is taking longer than usual to confirm. Check your dashboard in a few minutes — if
              the money left your account, your booking will appear there.
            </p>
            <Link
              to="/dashboard"
              data-testid="payment-pending-dashboard-link"
              className="mt-8 inline-block border border-white/20 px-8 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-aqua hover:text-aqua"
            >
              Go to my bookings
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
